import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const INITIAL_SMTP_FORM = {
  host: '',
  port: 587,
  username: '',
  password: '',
  secure: false,
  from_email: '',
  enabled: true,
  admin_password: '',
};

const INITIAL_SMS_FORM = {
  api_url: 'https://smsc.hubtel.com/v1/messages/send',
  sender_id: '',
  client_id: '',
  client_secret: '',
  enabled: true,
  admin_password: '',
};

const INITIAL_HUBTEL_PAYMENT_FORM = {
  client_id: '',
  client_secret: '',
  merchant_account: '',
  webhook_secret: '',
  checkout_base_url: 'https://checkout.hubtel.com/pay',
  enabled: true,
  admin_password: '',
};

const INITIAL_TWILIO_FORM = {
  account_sid: '',
  auth_token: '',
  from: '',
  enabled: true,
  admin_password: '',
};

export default function AdminIntegrations() {
  const [summary, setSummary] = useState(null);
  const [smtpForm, setSmtpForm] = useState(INITIAL_SMTP_FORM);
  const [smsForm, setSmsForm] = useState(INITIAL_SMS_FORM);
  const [hubtelPaymentForm, setHubtelPaymentForm] = useState(INITIAL_HUBTEL_PAYMENT_FORM);
  const [twilioForm, setTwilioForm] = useState(INITIAL_TWILIO_FORM);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingSms, setSavingSms] = useState(false);
  const [savingHubtelPayment, setSavingHubtelPayment] = useState(false);
  const [savingTwilio, setSavingTwilio] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);

  const loadIntegrations = async () => {
    try {
      const response = await axios.get('/api/admin/integrations', {
        headers: { 'Cache-Control': 'no-store' },
      });
      const data = response.data;
      setSummary(data);
      setSmtpForm({
        host: data.smtp?.host || '',
        port: data.smtp?.port || 587,
        username: '',
        password: '',
        secure: Boolean(data.smtp?.secure),
        from_email: data.smtp?.from_email || '',
        enabled: Boolean(data.smtp?.configured ? data.smtp.enabled : true),
        admin_password: '',
      });
      setSmsForm({
        api_url: data.hubtel_sms?.api_url || 'https://smsc.hubtel.com/v1/messages/send',
        sender_id: data.hubtel_sms?.sender_id || '',
        client_id: '',
        client_secret: '',
        enabled: Boolean(data.hubtel_sms?.configured ? data.hubtel_sms.enabled : true),
        admin_password: '',
      });
      setHubtelPaymentForm({
        client_id: '',
        client_secret: '',
        merchant_account: '',
        webhook_secret: '',
        checkout_base_url: data.hubtel_payment?.checkout_base_url || 'https://checkout.hubtel.com/pay',
        enabled: Boolean(data.hubtel_payment?.configured ? data.hubtel_payment.enabled : true),
        admin_password: '',
      });
      setTwilioForm({
        account_sid: '',
        auth_token: '',
        from: data.twilio_whatsapp?.from || '',
        enabled: Boolean(data.twilio_whatsapp?.configured ? data.twilio_whatsapp.enabled : true),
        admin_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load integrations');
    }
  };

  useEffect(() => {
    loadIntegrations();
  }, []);

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const response = await axios.put('/api/admin/integrations/smtp', smtpForm);
      toast.success(response.data.message || 'SMTP settings saved');
      await loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const saveSms = async () => {
    setSavingSms(true);
    try {
      const response = await axios.put('/api/admin/integrations/hubtel-sms', smsForm);
      toast.success(response.data.message || 'Hubtel SMS settings saved');
      await loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save Hubtel SMS settings');
    } finally {
      setSavingSms(false);
    }
  };

  const saveHubtelPayment = async () => {
    setSavingHubtelPayment(true);
    try {
      const response = await axios.put('/api/admin/integrations/hubtel-payment', hubtelPaymentForm);
      toast.success(response.data.message || 'Hubtel payment settings saved');
      await loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save Hubtel payment settings');
    } finally {
      setSavingHubtelPayment(false);
    }
  };

  const saveTwilio = async () => {
    setSavingTwilio(true);
    try {
      const response = await axios.put('/api/admin/integrations/twilio-whatsapp', twilioForm);
      toast.success(response.data.message || 'Twilio WhatsApp settings saved');
      await loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save Twilio WhatsApp settings');
    } finally {
      setSavingTwilio(false);
    }
  };

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post('/api/admin/integrations/test-email');
      toast.success(response.data.message || 'Test email sent');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const testSms = async () => {
    setTestingSms(true);
    try {
      const response = await axios.post('/api/admin/integrations/test-sms');
      toast.success(response.data.message || 'Test SMS sent');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send test SMS');
    } finally {
      setTestingSms(false);
    }
  };

  const testWhatsApp = async () => {
    setTestingWhatsApp(true);
    try {
      const response = await axios.post('/api/admin/integrations/test-whatsapp');
      toast.success(response.data.message || 'Test WhatsApp message sent');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send test WhatsApp message');
    } finally {
      setTestingWhatsApp(false);
    }
  };

  if (!summary) {
    return <div className="loading-center"><div className="spinner spinner-dark" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <Link to="/admin" className="back-btn">←</Link>
        <h1>Integrations</h1>
      </div>

      <div className="page-content">
        <div className="card" style={{ background: summary.secure_store_ready ? '#ecfdf5' : '#fef2f2', border: `1px solid ${summary.secure_store_ready ? '#86efac' : '#fca5a5'}` }}>
          <p style={{ fontWeight: 700, color: summary.secure_store_ready ? '#166534' : '#991b1b', marginBottom: 8 }}>
            {summary.secure_store_ready ? 'Secure credential storage is active' : 'Secure credential storage is not configured'}
          </p>
          <p style={{ fontSize: 13, color: summary.secure_store_ready ? '#166534' : '#991b1b', lineHeight: 1.6 }}>
            All sensitive integration credentials are encrypted at rest, never returned in full, and updates require your current admin password.
          </p>
        </div>

        <div className="card">
          <p style={{ fontWeight: 700, marginBottom: 10 }}>SMTP Email</p>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
            Status: <strong>{summary.smtp.configured ? 'Configured' : 'Not configured'}</strong> | Source: <strong>{summary.smtp.source}</strong>
          </p>
          {summary.smtp.username_masked && <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Saved username: <strong>{summary.smtp.username_masked}</strong></p>}
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
            <input type="checkbox" checked={smtpForm.enabled} onChange={(event) => setSmtpForm((previous) => ({ ...previous, enabled: event.target.checked }))} />
            <span>Enable SMTP notifications</span>
          </label>
          <div className="input-group"><label>SMTP Host</label><input className="input-field" value={smtpForm.host} onChange={(event) => setSmtpForm((previous) => ({ ...previous, host: event.target.value }))} /></div>
          <div className="input-group"><label>SMTP Port</label><input className="input-field" type="number" value={smtpForm.port} onChange={(event) => setSmtpForm((previous) => ({ ...previous, port: event.target.value }))} /></div>
          <div className="input-group"><label>SMTP Username</label><input className="input-field" autoComplete="off" placeholder={summary.smtp.username_masked ? `Leave blank to keep ${summary.smtp.username_masked}` : 'SMTP username'} value={smtpForm.username} onChange={(event) => setSmtpForm((previous) => ({ ...previous, username: event.target.value }))} /></div>
          <div className="input-group"><label>SMTP Password</label><input className="input-field" type="password" autoComplete="new-password" placeholder={summary.smtp.password_configured ? 'Leave blank to keep existing password' : 'SMTP password'} value={smtpForm.password} onChange={(event) => setSmtpForm((previous) => ({ ...previous, password: event.target.value }))} /></div>
          <div className="input-group"><label>From Email</label><input className="input-field" value={smtpForm.from_email} onChange={(event) => setSmtpForm((previous) => ({ ...previous, from_email: event.target.value }))} /></div>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
            <input type="checkbox" checked={smtpForm.secure} onChange={(event) => setSmtpForm((previous) => ({ ...previous, secure: event.target.checked }))} />
            <span>Use secure SMTP connection</span>
          </label>
          <div className="input-group"><label>Confirm Admin Password</label><input className="input-field" type="password" autoComplete="current-password" value={smtpForm.admin_password} onChange={(event) => setSmtpForm((previous) => ({ ...previous, admin_password: event.target.value }))} /></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" type="button" onClick={saveSmtp} disabled={savingSmtp} style={{ flex: 1 }}>{savingSmtp ? <span className="spinner" /> : 'Save SMTP'}</button>
            <button className="btn btn-outline" type="button" onClick={testEmail} disabled={testingEmail || !summary.smtp.configured} style={{ flex: 1 }}>{testingEmail ? <span className="spinner" /> : 'Send Test Email'}</button>
          </div>
        </div>

        <div className="card">
          <p style={{ fontWeight: 700, marginBottom: 10 }}>Hubtel SMS</p>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
            Status: <strong>{summary.hubtel_sms.configured ? 'Configured' : 'Not configured'}</strong> | Source: <strong>{summary.hubtel_sms.source}</strong>
          </p>
          {summary.hubtel_sms.client_id_masked && <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Saved client ID: <strong>{summary.hubtel_sms.client_id_masked}</strong></p>}
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
            <input type="checkbox" checked={smsForm.enabled} onChange={(event) => setSmsForm((previous) => ({ ...previous, enabled: event.target.checked }))} />
            <span>Enable Hubtel SMS notifications</span>
          </label>
          <div className="input-group"><label>Hubtel API URL</label><input className="input-field" value={smsForm.api_url} onChange={(event) => setSmsForm((previous) => ({ ...previous, api_url: event.target.value }))} /></div>
          <div className="input-group"><label>Sender ID</label><input className="input-field" value={smsForm.sender_id} onChange={(event) => setSmsForm((previous) => ({ ...previous, sender_id: event.target.value }))} /></div>
          <div className="input-group"><label>Client ID</label><input className="input-field" autoComplete="off" placeholder={summary.hubtel_sms.client_id_masked ? `Leave blank to keep ${summary.hubtel_sms.client_id_masked}` : 'Hubtel client ID'} value={smsForm.client_id} onChange={(event) => setSmsForm((previous) => ({ ...previous, client_id: event.target.value }))} /></div>
          <div className="input-group"><label>Client Secret</label><input className="input-field" type="password" autoComplete="new-password" placeholder={summary.hubtel_sms.client_secret_configured ? 'Leave blank to keep existing client secret' : 'Hubtel client secret'} value={smsForm.client_secret} onChange={(event) => setSmsForm((previous) => ({ ...previous, client_secret: event.target.value }))} /></div>
          <div className="input-group"><label>Confirm Admin Password</label><input className="input-field" type="password" autoComplete="current-password" value={smsForm.admin_password} onChange={(event) => setSmsForm((previous) => ({ ...previous, admin_password: event.target.value }))} /></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" type="button" onClick={saveSms} disabled={savingSms} style={{ flex: 1 }}>{savingSms ? <span className="spinner" /> : 'Save Hubtel SMS'}</button>
            <button className="btn btn-outline" type="button" onClick={testSms} disabled={testingSms || !summary.hubtel_sms.configured} style={{ flex: 1 }}>{testingSms ? <span className="spinner" /> : 'Send Test SMS'}</button>
          </div>
        </div>

        <div className="card">
          <p style={{ fontWeight: 700, marginBottom: 10 }}>Hubtel Payment</p>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
            Status: <strong>{summary.hubtel_payment.ready ? 'Ready' : summary.hubtel_payment.configured ? 'Incomplete' : 'Not configured'}</strong> | Source: <strong>{summary.hubtel_payment.source}</strong>
          </p>
          {summary.hubtel_payment.client_id_masked && <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Saved client ID: <strong>{summary.hubtel_payment.client_id_masked}</strong></p>}
          {summary.hubtel_payment.merchant_account_masked && <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Saved merchant account: <strong>{summary.hubtel_payment.merchant_account_masked}</strong></p>}
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
            <input type="checkbox" checked={hubtelPaymentForm.enabled} onChange={(event) => setHubtelPaymentForm((previous) => ({ ...previous, enabled: event.target.checked }))} />
            <span>Enable Hubtel direct payments</span>
          </label>
          <div className="input-group"><label>Client ID</label><input className="input-field" autoComplete="off" placeholder={summary.hubtel_payment.client_id_masked ? `Leave blank to keep ${summary.hubtel_payment.client_id_masked}` : 'Hubtel payment client ID'} value={hubtelPaymentForm.client_id} onChange={(event) => setHubtelPaymentForm((previous) => ({ ...previous, client_id: event.target.value }))} /></div>
          <div className="input-group"><label>Client Secret</label><input className="input-field" type="password" autoComplete="new-password" placeholder={summary.hubtel_payment.client_secret_configured ? 'Leave blank to keep existing client secret' : 'Hubtel payment client secret'} value={hubtelPaymentForm.client_secret} onChange={(event) => setHubtelPaymentForm((previous) => ({ ...previous, client_secret: event.target.value }))} /></div>
          <div className="input-group"><label>Merchant Account</label><input className="input-field" placeholder={summary.hubtel_payment.merchant_account_masked ? `Leave blank to keep ${summary.hubtel_payment.merchant_account_masked}` : 'Hubtel merchant account'} value={hubtelPaymentForm.merchant_account} onChange={(event) => setHubtelPaymentForm((previous) => ({ ...previous, merchant_account: event.target.value }))} /></div>
          <div className="input-group"><label>Webhook Secret</label><input className="input-field" type="password" autoComplete="new-password" placeholder={summary.hubtel_payment.webhook_secret_configured ? 'Leave blank to keep existing webhook secret' : 'Required webhook secret'} value={hubtelPaymentForm.webhook_secret} onChange={(event) => setHubtelPaymentForm((previous) => ({ ...previous, webhook_secret: event.target.value }))} /></div>
          <div className="input-group"><label>Checkout Base URL</label><input className="input-field" value={hubtelPaymentForm.checkout_base_url} onChange={(event) => setHubtelPaymentForm((previous) => ({ ...previous, checkout_base_url: event.target.value }))} /></div>
          <div className="input-group"><label>Confirm Admin Password</label><input className="input-field" type="password" autoComplete="current-password" value={hubtelPaymentForm.admin_password} onChange={(event) => setHubtelPaymentForm((previous) => ({ ...previous, admin_password: event.target.value }))} /></div>
          <button className="btn btn-primary btn-full" type="button" onClick={saveHubtelPayment} disabled={savingHubtelPayment}>
            {savingHubtelPayment ? <span className="spinner" /> : 'Save Hubtel Payment'}
          </button>
        </div>

        <div className="card">
          <p style={{ fontWeight: 700, marginBottom: 10 }}>Twilio WhatsApp</p>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
            Status: <strong>{summary.twilio_whatsapp.configured ? 'Configured' : 'Not configured'}</strong> | Source: <strong>{summary.twilio_whatsapp.source}</strong>
          </p>
          {summary.twilio_whatsapp.account_sid_masked && <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Saved account SID: <strong>{summary.twilio_whatsapp.account_sid_masked}</strong></p>}
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
            <input type="checkbox" checked={twilioForm.enabled} onChange={(event) => setTwilioForm((previous) => ({ ...previous, enabled: event.target.checked }))} />
            <span>Enable Twilio WhatsApp</span>
          </label>
          <div className="input-group"><label>Account SID</label><input className="input-field" autoComplete="off" placeholder={summary.twilio_whatsapp.account_sid_masked ? `Leave blank to keep ${summary.twilio_whatsapp.account_sid_masked}` : 'Twilio account SID'} value={twilioForm.account_sid} onChange={(event) => setTwilioForm((previous) => ({ ...previous, account_sid: event.target.value }))} /></div>
          <div className="input-group"><label>Auth Token</label><input className="input-field" type="password" autoComplete="new-password" placeholder={summary.twilio_whatsapp.auth_token_configured ? 'Leave blank to keep existing auth token' : 'Twilio auth token'} value={twilioForm.auth_token} onChange={(event) => setTwilioForm((previous) => ({ ...previous, auth_token: event.target.value }))} /></div>
          <div className="input-group"><label>WhatsApp Sender</label><input className="input-field" placeholder="whatsapp:+14155238886" value={twilioForm.from} onChange={(event) => setTwilioForm((previous) => ({ ...previous, from: event.target.value }))} /></div>
          <div className="input-group"><label>Confirm Admin Password</label><input className="input-field" type="password" autoComplete="current-password" value={twilioForm.admin_password} onChange={(event) => setTwilioForm((previous) => ({ ...previous, admin_password: event.target.value }))} /></div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" type="button" onClick={saveTwilio} disabled={savingTwilio} style={{ flex: 1 }}>{savingTwilio ? <span className="spinner" /> : 'Save Twilio WhatsApp'}</button>
            <button className="btn btn-outline" type="button" onClick={testWhatsApp} disabled={testingWhatsApp || !summary.twilio_whatsapp.configured} style={{ flex: 1 }}>{testingWhatsApp ? <span className="spinner" /> : 'Send Test WhatsApp'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
